import { loadStripe, Stripe } from '@stripe/stripe-js';
import { APP_ID, createOrUpdateSubscription, getSupabase, getUserSubscription } from './supabase';
import type { SubscriptionTier, Subscription } from '../types';
import { logger } from '../utils/logger';

// Stripe publishable key from environment
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_live_51SPMyFBd3ZF7TG3mA5RzbmbAGnMdLgmFJ49l0nb5HkD3GGfphktreiJviIa8P4s9rEWMdwhwkZ8kLmcn1GtuwYls00xPxeppsz';

// Supabase Edge Functions base URL
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const FUNCTIONS_URL = SUPABASE_URL ? `${SUPABASE_URL}/functions/v1` : '';

// Price IDs from environment (set in Stripe Dashboard)
const STRIPE_PRICES: Record<Exclude<SubscriptionTier, 'free'>, string | undefined> = {
  light: import.meta.env.VITE_STRIPE_PRICE_ID_LIGHT,
  standard: import.meta.env.VITE_STRIPE_PRICE_ID_STANDARD,
  enterprise: import.meta.env.VITE_STRIPE_PRICE_ID_ENTERPRISE
};

// Singleton Stripe instance
let stripePromise: Promise<Stripe | null> | null = null;

/**
 * Get authorization header for Edge Functions
 */
async function getAuthHeader(): Promise<string | null> {
  const supabase = getSupabase();
  if (!supabase) return null;

  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ? `Bearer ${session.access_token}` : null;
}

/**
 * Get or initialize Stripe client
 */
export function getStripe(): Promise<Stripe | null> {
  if (!stripePromise) {
    if (!STRIPE_PUBLISHABLE_KEY) {
      logger.warn('Stripe publishable key not found. Payment features disabled.');
      return Promise.resolve(null);
    }
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
}

/**
 * Check if Stripe is configured
 */
export function isStripeConfigured(): boolean {
  return !!STRIPE_PUBLISHABLE_KEY;
}

/**
 * Get price ID for a tier
 */
export function getPriceIdForTier(tier: SubscriptionTier): string | null {
  if (tier === 'free') return null;
  return STRIPE_PRICES[tier] || null;
}

/**
 * Create a Checkout session for subscription
 * Uses Supabase Edge Function to create the session securely
 */
export async function createCheckoutSession(
  tier: Exclude<SubscriptionTier, 'free'>,
  organizationId: string,
  successUrl: string,
  cancelUrl: string,
  /** 席数。未指定は1。金額に直結するのでサーバー側でも1〜50の整数を検証する。 */
  seats?: number
): Promise<{ sessionId: string; url: string; error?: string } | null> {
  const priceId = getPriceIdForTier(tier);
  if (!priceId) {
    logger.error(`No price ID configured for tier: ${tier}`);
    logger.error('Available price IDs:', STRIPE_PRICES);
    return { sessionId: '', url: '', error: `価格IDが設定されていません: ${tier}` };
  }

  const authHeader = await getAuthHeader();
  if (!authHeader) {
    logger.error('Not authenticated - no auth header');
    return { sessionId: '', url: '', error: '認証されていません。再ログインしてください。' };
  }

  try {
    logger.log('Creating checkout session:', { tier, organizationId, priceId });

    // Call Supabase Edge Function to create checkout session
    const response = await fetch(`${FUNCTIONS_URL}/genbasafeai_stripe-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader
      },
      body: JSON.stringify({
        priceId,
        organizationId,
        tier,
        successUrl,
        cancelUrl,
        seats
      })
    });

    logger.log('Checkout response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      logger.error('Checkout API error:', errorData);
      return { sessionId: '', url: '', error: errorData.error || `サーバーエラー (${response.status})` };
    }

    const data = await response.json();
    return {
      sessionId: data.sessionId,
      url: data.url
    };
  } catch (error) {
    logger.error('Error creating checkout session:', error);
    return { sessionId: '', url: '', error: error instanceof Error ? error.message : '不明なエラー' };
  }
}

/**
 * Redirect to Stripe Checkout
 */
export async function redirectToCheckout(sessionId: string): Promise<void> {
  const stripe = await getStripe();
  if (!stripe) {
    throw new Error('Stripe not initialized');
  }

  const { error } = await stripe.redirectToCheckout({ sessionId });
  if (error) {
    throw error;
  }
}

/**
 * Create a Customer Portal session for managing subscription
 * Uses Supabase Edge Function
 */
export async function createPortalSession(
  returnUrl: string
): Promise<{ url: string } | null> {
  const authHeader = await getAuthHeader();
  if (!authHeader) {
    logger.error('Not authenticated');
    return null;
  }

  try {
    const response = await fetch(`${FUNCTIONS_URL}/genbasafeai_stripe-portal`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader
      },
      body: JSON.stringify({
        returnUrl
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create portal session');
    }

    return await response.json();
  } catch (error) {
    logger.error('Error creating portal session:', error);
    return null;
  }
}

/**
 * Handle successful checkout completion
 *
 * プラン付与（organizations.plan / qr_limit の書き換え）はここでは行わない。
 * 付与は genbasafeai_stripe-webhook（Stripe 署名検証済み・service_role）の
 * 専権とし、クライアントは結果を読むだけにする。
 *
 * 以前はここから updateOrganizationPlan を呼んでいたが、これはクライアントに
 * 課金列の書き込み権を要求する経路であり、決済なしで plan='enterprise' に
 * できる穴（監査 C-1）そのものだった。migration 019 で DB 側の権限も外した
 * ため、呼び出しを残しても permission denied になる。
 *
 * この関数の役割は「webhook が反映した購読情報をローカルに読み直す」だけ。
 */
export async function handleCheckoutSuccess(
  userId: string,
  _organizationId: string
): Promise<boolean> {
  // The webhook will have already updated the subscription and the organization
  // plan. Just refresh the local user subscription data.
  const subscription = await getUserSubscription(userId);

  return !!subscription;
}

/**
 * Open Customer Portal to manage subscription
 * Users can cancel, resume, or change their plan there
 */
export async function openCustomerPortal(returnUrl: string): Promise<boolean> {
  const result = await createPortalSession(returnUrl);
  if (result?.url) {
    window.location.href = result.url;
    return true;
  }
  return false;
}

/**
 * Format price for display
 */
export function formatPrice(price: number, currency: string = 'JPY'): string {
  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0
  }).format(price);
}

/**
 * Check if tier upgrade is available
 */
export function canUpgradeTo(currentTier: SubscriptionTier, targetTier: SubscriptionTier): boolean {
  const tierOrder: SubscriptionTier[] = ['free', 'light', 'standard', 'enterprise'];
  return tierOrder.indexOf(targetTier) > tierOrder.indexOf(currentTier);
}

/**
 * Check if tier downgrade is available
 */
export function canDowngradeTo(currentTier: SubscriptionTier, targetTier: SubscriptionTier): boolean {
  const tierOrder: SubscriptionTier[] = ['free', 'light', 'standard', 'enterprise'];
  return tierOrder.indexOf(targetTier) < tierOrder.indexOf(currentTier);
}
